import bcrypt from 'bcryptjs'
import db from './offlineDb'

// Hash password — try Web Worker first, fall back to main thread
const hashPassword = (password) => {
  return new Promise((resolve, reject) => {
    try {
      const worker = new Worker(new URL('../workers/bcryptWorker.js', import.meta.url))
      const timeout = setTimeout(() => {
        worker.terminate()
        // Fallback to main thread
        resolve(bcrypt.hashSync(password, 10))
      }, 5000)

      worker.onmessage = (e) => {
        clearTimeout(timeout)
        if (e.data.type === 'hash') {
          resolve(e.data.result)
          worker.terminate()
        }
      }
      worker.onerror = () => {
        clearTimeout(timeout)
        worker.terminate()
        // Fallback to main thread
        resolve(bcrypt.hashSync(password, 10))
      }
      worker.postMessage({ type: 'hash', password })
    } catch {
      // Worker creation failed, use main thread
      resolve(bcrypt.hashSync(password, 10))
    }
  })
}

// Compare password — try Web Worker first, fall back to main thread
const comparePassword = (password, hash) => {
  return new Promise((resolve, reject) => {
    try {
      const worker = new Worker(new URL('../workers/bcryptWorker.js', import.meta.url))
      const timeout = setTimeout(() => {
        worker.terminate()
        resolve(bcrypt.compareSync(password, hash))
      }, 5000)

      worker.onmessage = (e) => {
        clearTimeout(timeout)
        if (e.data.type === 'compare') {
          resolve(e.data.result)
          worker.terminate()
        }
      }
      worker.onerror = () => {
        clearTimeout(timeout)
        worker.terminate()
        resolve(bcrypt.compareSync(password, hash))
      }
      worker.postMessage({ type: 'compare', password, hash })
    } catch {
      resolve(bcrypt.compareSync(password, hash))
    }
  })
}

// Cache user credentials after successful online login
export const cacheUserForOffline = async (email, password, userData, token) => {
  // Clear any previous cached user (only 1 user cached at a time)
  await db.auth.clear()

  const passwordHash = await hashPassword(password)

  await db.auth.put({
    email: email.toLowerCase(),
    userId: userData.id,
    passwordHash,
    userData,
    token,
    cachedAt: new Date().toISOString()
  })
}

// Attempt offline login — returns { userData, token } or throws
export const offlineLogin = async (email, password) => {
  const cached = await db.auth.get(email.toLowerCase())

  if (!cached) {
    throw new Error('No cached credentials found. Please connect to the internet to log in.')
  }

  const match = await comparePassword(password, cached.passwordHash)
  if (!match) {
    throw new Error('Invalid email or password.')
  }

  return {
    userData: cached.userData,
    token: cached.token
  }
}

// Clear cached auth on logout
export const clearOfflineAuth = async () => {
  await db.auth.clear()
}

export default { cacheUserForOffline, offlineLogin, clearOfflineAuth }
